'use client'

import type { PropsWithChildren, ReactNode } from 'react'

import { useResourceParams, useUserFriendlyName } from '@refinedev/core'
import { CreateButton } from '~/components/refineUi/buttons/create'
import { Breadcrumb } from '~/components/refineUi/layout/breadcrumb'
import { Separator } from '~/components/ui/separator'
import { cn } from '~/lib/utils'

type ListViewProps = PropsWithChildren<{
  className?: string
}>

export function ListView({ children, className }: ListViewProps) {
  return (
    <div className={cn('flex flex-col', 'gap-4', className)}>{children}</div>
  )
}

type ListHeaderProps = PropsWithChildren<{
  resource?: string
  title?: string
  canCreate?: boolean
  /** more buttons next to Create (e.g. a dialog that creates) */
  actions?: ReactNode
  headerClassName?: string
  wrapperClassName?: string
}>

export const ListViewHeader = ({
  canCreate,
  actions,
  resource: resourceFromProps,
  title: titleFromProps,
  wrapperClassName,
  headerClassName,
}: ListHeaderProps) => {
  const getUserFriendlyName = useUserFriendlyName()

  const { resource, identifier } = useResourceParams({
    resource: resourceFromProps,
  })
  const resourceName = identifier ?? resource?.name

  const isCreateButtonVisible = canCreate ?? Boolean(resource?.create)

  const title =
    titleFromProps ??
    getUserFriendlyName(
      resource?.meta?.label ?? identifier ?? resource?.name,
      'plural',
    )

  return (
    <div className={cn('flex flex-col', 'gap-4', wrapperClassName)}>
      <div className="flex items-center relative gap-2">
        <div className="bg-background z-[2] pr-4">
          <Breadcrumb />
        </div>
        <Separator className={cn('absolute', 'left-0', 'right-0', 'z-[1]')} />
      </div>
      <div className={cn('flex', 'justify-between', 'gap-4', headerClassName)}>
        <h2 className="text-2xl font-bold">{title}</h2>
        {(isCreateButtonVisible || actions !== undefined) && (
          <div className="flex items-center gap-2">
            {actions}
            {isCreateButtonVisible && <CreateButton resource={resourceName} />}
          </div>
        )}
      </div>
    </div>
  )
}

ListView.displayName = 'ListView'
